import {
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from "@mui/material";
import { DialogProps } from "@toolpad/core/useDialogs";
import { Button } from "../buttons/Button";

export interface IConfirmationDialogProps {
    /**
     * The title of the dialog
     */
    title: string;
    /**
     * The description of the dialog
     */
    description: string;
    /**
     * The text for the confirm button
     */
    confirmText?: string;
    /**
     * Whether the dialog is destructive
     *
     * If true, the confirm button will be red
     *
     * @default false
     */
    destructive?: boolean;
    /**
     * The function to call when the user confirms the dialog
     */
    onConfirm: () => void;
}

/**
 * A confirmation dialog that allows the user to confirm or cancel an action
 */
export const ConfirmationDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IConfirmationDialogProps>) => {
    const {
        title,
        description,
        confirmText = "Yes",
        destructive = false,
        onConfirm,
    } = payload;

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogContent sx={{ p: 5 }}>
                <Stack alignItems={"center"}>
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ textAlign: "center" }}
                        gutterBottom
                    >
                        {title}
                    </Typography>
                    <Typography sx={{ textAlign: "center" }}>
                        {description}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 5, pb: 5, justifyContent: "center" }}>
                <Button
                    onClick={() => onClose()}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        color: "text.primary",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={destructive ? "error" : "primary"}
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        textTransform: "capitalize",
                    }}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
